/**
 * Card Component
 *
 * A container component with subtle shadow, rounded corners, and padding options.
 * Supports different visual variants and interactive states.
 *
 * Validates: Requirements 1.2, 1.3, 1.6
 * - Requirement 1.2: THE Console SHALL use modern UI patterns including cards
 *   with subtle shadows, rounded corners, and smooth transitions.
 * - Requirement 1.3: WHEN a user interacts with interactive elements, THE Console
 *   SHALL provide visual feedback within 100ms.
 * - Requirement 1.6: THE Console SHALL use accessible color contrasts meeting
 *   WCAG 2.1 AA standards for all text elements.
 */

import React, { forwardRef, useState } from 'react';
import { tokens } from '../tokens';

export type CardVariant = 'elevated' | 'outlined' | 'filled';
export type CardPadding = 'none' | 'sm' | 'md' | 'lg';

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Visual style variant */
  variant?: CardVariant;
  /** Padding size */
  padding?: CardPadding;
  /** Whether the card is interactive (clickable) */
  interactive?: boolean;
  /** Whether the card takes full width */
  fullWidth?: boolean;
  /** Custom border radius */
  borderRadius?: keyof typeof tokens.borderRadius;
}

/**
 * Get padding styles based on size
 */
function getPaddingStyles(padding: CardPadding): React.CSSProperties {
  switch (padding) {
    case 'none':
      return { padding: 0 };
    case 'sm':
      return { padding: tokens.spacing[3] };
    case 'lg':
      return { padding: tokens.spacing[6] };
    case 'md':
    default:
      return { padding: tokens.spacing[4] };
  }
}

/**
 * Get variant-specific styles
 */
function getVariantStyles(variant: CardVariant): React.CSSProperties {
  switch (variant) {
    case 'outlined':
      return {
        backgroundColor: tokens.colors.neutral[50],
        border: `1px solid ${tokens.colors.neutral[200]}`,
        boxShadow: 'none',
      };
    case 'filled':
      return {
        backgroundColor: tokens.colors.neutral[100],
        border: 'none',
        boxShadow: 'none',
      };
    case 'elevated':
    default:
      return {
        backgroundColor: tokens.colors.neutral[50],
        border: 'none',
        boxShadow: tokens.shadows.md,
      };
  }
}

/**
 * Get hover styles for interactive cards
 */
function getHoverStyles(variant: CardVariant): React.CSSProperties {
  switch (variant) {
    case 'outlined':
      return {
        borderColor: tokens.colors.neutral[300],
        boxShadow: tokens.shadows.sm,
      };
    case 'filled':
      return {
        backgroundColor: tokens.colors.neutral[200],
      };
    case 'elevated':
    default:
      return {
        boxShadow: tokens.shadows.accent,
        transform: 'translateY(-2px)',
        borderColor: tokens.colors.accent[100],
      };
  }
}

/**
 * Card component for grouping related content.
 *
 * Features:
 * - Three visual variants: elevated (with shadow), outlined, and filled
 * - Configurable padding sizes
 * - Optional interactive state with hover effects
 * - Smooth transitions for visual feedback
 *
 * @example
 * ```tsx
 * // Basic elevated card
 * <Card>
 *   <h3>Card Title</h3>
 *   <p>Card content goes here.</p>
 * </Card>
 *
 * // Outlined card with large padding
 * <Card variant="outlined" padding="lg">
 *   <p>Content with more spacing</p>
 * </Card>
 *
 * // Interactive card (clickable)
 * <Card interactive onClick={handleClick}>
 *   <p>Click me!</p>
 * </Card>
 * ```
 */
export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  {
    variant = 'elevated',
    padding = 'md',
    interactive = false,
    fullWidth = false,
    borderRadius = 'lg',
    children,
    style,
    onClick,
    onMouseEnter,
    onMouseLeave,
    onKeyDown,
    ...props
  },
  ref
) {
  const [isHovered, setIsHovered] = useState(false);

  const isClickable = interactive || Boolean(onClick);

  const baseStyles: React.CSSProperties = {
    borderRadius: tokens.borderRadius[borderRadius],
    // Transition for visual feedback within 100ms (using 150ms fast token)
    transition: `all ${tokens.transitions.fast}`,
    width: fullWidth ? '100%' : 'auto',
    cursor: isClickable ? 'pointer' : 'default',
    outline: 'none',
    ...getPaddingStyles(padding),
    ...getVariantStyles(variant),
    ...(isHovered && isClickable ? getHoverStyles(variant) : {}),
    ...style,
  };

  // Handle keyboard interaction for accessibility
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onClick?.(e as unknown as React.MouseEvent<HTMLDivElement>);
    }
    onKeyDown?.(e);
  };

  return (
    <div
      ref={ref}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      style={baseStyles}
      onClick={onClick}
      onMouseEnter={(e) => {
        setIsHovered(true);
        onMouseEnter?.(e);
      }}
      onMouseLeave={(e) => {
        setIsHovered(false);
        onMouseLeave?.(e);
      }}
      onKeyDown={handleKeyDown}
      {...props}
    >
      {children}
    </div>
  );
});

/**
 * Card.Header component for card titles and actions
 */
export interface CardHeaderProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Title text */
  title?: React.ReactNode;
  /** Subtitle text */
  subtitle?: React.ReactNode;
  /** Action elements (buttons, icons, etc.) */
  action?: React.ReactNode;
}

export const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(
  function CardHeader({ title, subtitle, action, children, style, ...props }, ref) {
    const headerStyles: React.CSSProperties = {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: tokens.spacing[4],
      marginBottom: tokens.spacing[4],
      ...style,
    };

    const titleContainerStyles: React.CSSProperties = {
      flex: 1,
      minWidth: 0,
    };

    const titleStyles: React.CSSProperties = {
      fontFamily: tokens.typography.fontFamily.sans,
      fontSize: tokens.typography.fontSize.lg,
      fontWeight: tokens.typography.fontWeight.semibold,
      color: tokens.colors.neutral[900],
      margin: 0,
      lineHeight: tokens.typography.lineHeight.tight,
    };

    const subtitleStyles: React.CSSProperties = {
      fontFamily: tokens.typography.fontFamily.sans,
      fontSize: tokens.typography.fontSize.sm,
      color: tokens.colors.neutral[500],
      margin: 0,
      marginTop: tokens.spacing[1],
      lineHeight: tokens.typography.lineHeight.normal,
    };

    if (children) {
      return (
        <div ref={ref} style={headerStyles} {...props}>
          {children}
        </div>
      );
    }

    return (
      <div ref={ref} style={headerStyles} {...props}>
        <div style={titleContainerStyles}>
          {title && <h3 style={titleStyles}>{title}</h3>}
          {subtitle && <p style={subtitleStyles}>{subtitle}</p>}
        </div>
        {action && <div>{action}</div>}
      </div>
    );
  }
);

/**
 * Card.Body component for main card content
 */
export const CardBody = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(function CardBody({ children, style, ...props }, ref) {
  const bodyStyles: React.CSSProperties = {
    fontFamily: tokens.typography.fontFamily.sans,
    fontSize: tokens.typography.fontSize.base,
    color: tokens.colors.neutral[700],
    lineHeight: tokens.typography.lineHeight.normal,
    ...style,
  };

  return (
    <div ref={ref} style={bodyStyles} {...props}>
      {children}
    </div>
  );
});

/**
 * Card.Footer component for card actions
 */
export const CardFooter = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(function CardFooter({ children, style, ...props }, ref) {
  const footerStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacing[2],
    marginTop: tokens.spacing[4],
    paddingTop: tokens.spacing[4],
    borderTop: `1px solid ${tokens.colors.neutral[200]}`,
    ...style,
  };

  return (
    <div ref={ref} style={footerStyles} {...props}>
      {children}
    </div>
  );
});

export default Card;
